import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Button from './Button';

gsap.registerPlugin(ScrollTrigger);

const Services = () => {
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [cardDimensions, setCardDimensions] = useState({ width: 380, height: 480 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const services = [
    {
      title: "Sheet Laser Cutting",
      slug: "sheet-laser-cutting",
      description: "Precision flat sheet laser cutting for MS up to 22mm, SS up to 16mm, copper & aluminum. BODOR Fiber Laser technology with ±0.1mm accuracy.",
      subServices: ["MS Sheets", "SS Sheets", "Aluminum", "Copper"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/flatsheet2.png"
    },
    {
      title: "Pipe Laser Cutting",
      slug: "pipe-laser-cutting",
      description: "Advanced laser pipe and tube cutting services. Round, square & profile cutting with high precision for industrial applications.",
      subServices: ["Round Pipes", "Square Tubes", "Profile Cutting"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/MVI_2544.00_00_33_05.Still012.png"
    },
    {
      title: "CNC Bending",
      slug: "cnc-bending",
      description: "Advanced CNC sheet bending services with 200 TON capacity and 4300mm length. DELEM controller for precise bending operations.",
      subServices: ["Sheet Bending", "Box Bending", "Channel Bending"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/CNC/MVI_2544.00_00_51_26.Still017.png"
    },
    {
      title: "Laser Welding",
      slug: "laser-welding",
      description: "High-precision laser welding for thin materials and intricate joints. Minimal heat distortion with excellent weld quality.",
      subServices: ["Spot Welding", "Seam Welding", "Micro Welding"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/LaserWelding/welding.jpeg"
    },
    {
      title: "TIG Welding",
      slug: "tig-welding",
      description: "Professional TIG welding for high-quality, clean welds on stainless steel, aluminum & exotic metals. Precise control for critical applications.",
      subServices: ["SS Welding", "Aluminum Welding", "Precision Joints"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      image: "https://www.millerwelds.com/-/media/miller-electric/images/article-images/3/b/d/tig-it_feature.jpg?h=720&w=1280&la=en&hash=0CF9719BFD95AB8E298E664B37DF85C7"
    },
    {
      title: "MIG Welding",
      slug: "mig-welding",
      description: "Fast and efficient MIG welding for high-volume production. AOTAI machines with 250-400 AMPS for industrial fabrication.",
      subServices: ["MS Welding", "Heavy Fabrication", "Structural Welding"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Tig&Mig/Mig-Welding.jpeg"
    },
    {
      title: "Rolling",
      slug: "rolling",
      description: "Sheet and plate rolling services for cylindrical and conical shapes. Precision rolling for tanks, ducts & custom curved components.",
      subServices: ["Sheet Rolling", "Plate Rolling", "Cone Rolling"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Rolling/image.png"
    },
    {
      title: "Drilling & Tapping",
      slug: "drilling-tapping",
      description: "Precision drilling and tapping services for threaded holes. CNC drilling for accurate hole placement and consistent threading.",
      subServices: ["CNC Drilling", "Thread Tapping", "Counter Boring"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Drilling/MVI_2544.00_00_24_08.Still007.png"
    },
    {
      title: "Riveting",
      slug: "riveting",
      description: "Professional stud and standoff riveting services. Secure fastening solutions for sheet metal assemblies and enclosures.",
      subServices: ["Stud Riveting", "Standoff Installation", "PEM Fasteners"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/riveting/af6f7f92ff35.jpeg"
    },
    {
      title: "Buffing",
      slug: "buffing",
      description: "Mirror finish buffing and polishing services. Surface finishing for stainless steel, aluminum & other metals to achieve premium aesthetics.",
      subServices: ["Mirror Finish", "Satin Finish", "Metal Polishing"],
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      image: "https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/buffing/polish-wheel-1-500x500.webp"
    }
  ];

  useEffect(() => {
    // Update card dimensions based on viewport
    const updateDimensions = () => {
      const vw = window.innerWidth;
      setIsMobile(vw < 640);
      if (vw < 640) {
        setCardDimensions({ width: 260, height: 360 });
      } else if (vw < 1024) {
        setCardDimensions({ width: 320, height: 420 });
      } else if (vw < 1440) {
        // MacBook view - smaller cards
        setCardDimensions({ width: 300, height: 400 });
      } else {
        setCardDimensions({ width: 380, height: 480 });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    // Only apply GSAP horizontal scroll on desktop
    if (window.innerWidth >= 640) {
      const section = sectionRef.current;
      const scrollContainer = scrollContainerRef.current;
      
      if (!section || !scrollContainer) return;

      const cards = scrollContainer.querySelectorAll('.service-card');
      const viewportWidth = window.innerWidth;

      // Set ScrollTrigger config to not affect fixed elements
      ScrollTrigger.config({
        ignoreMobileResize: true,
        autoRefreshEvents: "visibilitychange,DOMContentLoaded,load"
      });

      // Cards are already centered via CSS padding, no initial offset needed
      const initialOffset = 0;

      // Set initial position
      gsap.set(scrollContainer, { x: initialOffset });

      // Calculate the end position so last card (Packaging & Delivery) is centered
      // Total cards = 10, last card index = 9
      const cardWidth = cards[0]?.offsetWidth || 500;
      const gap = 32; // gap-8 = 2rem = 32px
      const lastCardIndex = cards.length - 1;
      
      // Position where last card is centered: 
      // We need to scroll so the last card's center aligns with viewport center
      const lastCardPosition = lastCardIndex * (cardWidth + gap);
      const endX = lastCardPosition - (viewportWidth / 2) + (cardWidth / 2);

      // Create horizontal scroll animation
      gsap.to(scrollContainer, {
        x: -endX,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${endX + viewportWidth}`,
          scrub: 1,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          fastScrollEnd: true,
        }
      });

      // Animate cards appearing one by one - cards visible immediately
      cards.forEach((card, index) => {
        // Set cards to be visible from the start
        gsap.set(card, {
          opacity: 1,
          y: 0,
          scale: 1
        });
      });
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section id="services" ref={sectionRef} className="pt-16 pb-10 bg-gray-50 overflow-hidden relative z-0">
      <div className="max-w-7xl mx-auto px-6 mb-6">
        {/* Header */}
        <motion.div 
          className="text-center space-y-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-3">
            <p className="text-primary font-semibold uppercase tracking-wide text-sm">
              Service
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-steel font-poppins leading-tight">
              Our Cutting & Fabrication Services
            </h2>
          </div>
          <p className="text-lg lg:text-xl text-steel-lighter max-w-3xl mx-auto">
            High-quality laser solutions designed to meet every industrial demand.
          </p>
        </motion.div>
      </div>

      {/* Horizontal Scrolling Container - Desktop / Swipe Carousel - Mobile */}
      <div className="relative">
        {isMobile ? (
          // Mobile: Swipe Carousel
          <div className="relative overflow-hidden">
            <motion.div
              ref={scrollContainerRef}
              className="flex gap-4 px-4"
              drag="x"
              dragConstraints={{ 
                left: -(services.length - 1) * (cardDimensions.width + 16), 
                right: 0 
              }}
              dragElastic={0.1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = Math.abs(offset.x) * velocity.x;
                if (swipe < -500 && currentIndex < services.length - 1) {
                  setCurrentIndex(currentIndex + 1);
                } else if (swipe > 500 && currentIndex > 0) {
                  setCurrentIndex(currentIndex - 1);
                }
              }}
              animate={{ x: -currentIndex * (cardDimensions.width + 16) }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {services.map((service, index) => (
                <Link
                  key={index}
                  to={`/services/${service.slug}`}
                  className="service-card group cursor-pointer overflow-hidden rounded-xl relative block"
                  style={{ 
                    width: `${cardDimensions.width}px`,
                    height: `${cardDimensions.height}px`,
                    flexShrink: 0
                  }}
                >
                  {/* Service Image */}
                  <div className="w-full h-full overflow-hidden relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-steel/90 via-steel/50 to-transparent"></div>
                    
                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-xl font-bold text-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </motion.div>
            
            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {services.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-primary w-8' 
                      : 'bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          // Desktop: Horizontal Scroll
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-6 lg:gap-8"
            style={{ 
              width: 'max-content',
              paddingLeft: `calc((100vw - ${cardDimensions.width}px) / 2)`,
              paddingRight: `calc((100vw - ${cardDimensions.width}px) / 2)`
            }}
          >
            {services.map((service, index) => (
              <Link
                key={index}
                to={`/services/${service.slug}`}
                className="service-card group cursor-pointer overflow-hidden rounded-xl sm:rounded-2xl relative block hover:-translate-y-2 transition-transform duration-300"
                style={{ 
                  width: `${cardDimensions.width}px`,
                  height: `${cardDimensions.height}px`,
                  flexShrink: 0
                }}
              >
                {/* Service Image */}
                <div 
                  className="w-full h-full overflow-hidden relative"
                >
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-steel/90 via-steel/50 to-transparent"></div>
                  
                  {/* Title overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
                    <h3 
                      className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-white group-hover:-translate-y-1 transition-transform duration-200"
                    >
                      {service.title}
                    </h3>
                  </div>
                  
                  {/* Hover overlay icon */}
                  <div 
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CTA Button */}
      <div className="text-center mt-12 sm:mt-16 lg:mt-20 px-4">
        <Link to="/services">
          <Button variant="secondary" size="lg" className="text-base sm:text-lg px-6 sm:px-10 py-3 sm:py-4">
            View All Services
          </Button>
        </Link>
      </div>
    </section>
  );
};

export default Services;